
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Skeleton } from '@/components/ui/skeleton';

type ProtectedRouteProps = {
  children: React.ReactNode;
  allowedRoles?: ('admin' | 'organization' | 'public' | 'administrator' | 'organization_user')[];
  requireAuth?: boolean;
};

const ProtectedRoute = ({ 
  children, 
  allowedRoles = [], 
  requireAuth = true 
}: ProtectedRouteProps) => {
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    // Only process when loading is complete
    if (loading) return;
    
    console.log("ProtectedRoute: Loading complete", { 
      user: user?.email, 
      profileRole: profile?.role, 
      requireAuth, 
      allowedRoles 
    });

    // Check if authentication is required but user is not logged in
    if (requireAuth && !user) {
      console.log("Authentication required but user not logged in, redirecting to login");
      navigate('/login', { replace: true });
      return;
    }

    // Check if role-based access is needed
    if (allowedRoles.length > 0 && profile && !allowedRoles.includes(profile.role)) {
      console.log(`User role ${profile.role} not allowed, redirecting`);
      
      // Redirect based on role - handle both old and new roles
      switch (profile.role) {
        case 'admin':
        case 'administrator':
          navigate('/admin', { replace: true });
          break;
        case 'organization':
        case 'organization_user':
          navigate('/organization', { replace: true });
          break;
        default:
          navigate('/', { replace: true });
          break;
      }
      return;
    }

    // User is authorized to view this route
    console.log("User is authorized to view this route");
    setAuthorized(true);
  }, [user, profile, loading, navigate, requireAuth, allowedRoles]);

  if (loading) {
    return (
      <div className="flex flex-col space-y-4 p-8">
        <Skeleton className="h-8 w-[250px]" />
        <Skeleton className="h-6 w-[300px]" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <Skeleton className="h-[150px] w-full" />
          <Skeleton className="h-[150px] w-full" />
          <Skeleton className="h-[150px] w-full" />
        </div>
      </div>
    );
  }

  return authorized ? <>{children}</> : null;
};

export default ProtectedRoute;
